<script setup lang="ts">
/**
 * The content slide-over: an article, trade alert or webinar over the page it was opened from. Mounted once, in
 * App.vue, outside <router-view>, so it outlives page changes; the address drives it (UX roadmap #14): ?preview= opens
 * it, Back closes it, and a link inside it to another page closes it.
 */
import { computed, shallowRef, watch } from "vue";
import { useWindowSize } from "@vueuse/core";

import ContentSkeleton from "@/components/layout/ContentSkeleton.vue";
import { useCompactViewport } from "@/composables/useCompactViewport";
import pinia from "@/plugins/PiniaPlugin";
import { useApplicationStore } from "@/stores/Application";
import { useCloseOverlaysOnNavigation } from "@/composables/useCloseOverlaysOnNavigation";
import { useContentPreviewRoute } from "@/composables/useContentPreviewRoute";
import { lazyView } from "@/utils/lazy-view";

import type { ContentfulItem } from "@tmi/types/entities";

// Renders only inside the open Drawer.
const ContentModal = lazyView( () => import( "@/components/modals/ContentModal.vue" ) );

useCloseOverlaysOnNavigation();
const { drawerVisible, isResolving } = useContentPreviewRoute();

const appStore = useApplicationStore( pinia );
const hasContent = computed< boolean >( () => !!appStore.contentItem );
// ContentModal reads the item once, in setup: a second item opened over the first gets a fresh one. The last key is
// kept so the item stays on screen while the drawer slides out, rather than an empty panel.
const modalKey = shallowRef< string >( '' );
watch( () => ( appStore.contentItem as ContentfulItem | undefined )?.contentfulId, ( id: string | undefined ) => {
  if ( id ) modalKey.value = `${ appStore.contentKey }:${ id }`;
}, { immediate: true } );
const showModal = computed< boolean >( () => hasContent.value || ( !drawerVisible.value && !!modalKey.value ) );

const { width } = useWindowSize();
// UX roadmap #29: a phone on its side (568-932px) gets the full-screen item too; 85% of it left a strip of page, the
// drawer's own header row and the desktop item layout on a screen about 390px tall. Below md stays as it was.
const { isCompactViewport } = useCompactViewport();
const smallScreenSize = computed< boolean >( () => width.value < 768 || isCompactViewport.value );
// UX roadmap #25: full-screen on phones, the item's own sticky bar carries the close, so the drawer's header (a lone X
// in its own row) goes. Only once the item is on screen: the skeleton, and the error if its chunk fails, have no close.
const modalReady = shallowRef< boolean >( false );
watch( modalKey, () => { modalReady.value = false; } );
const compactBar = computed< boolean >( () => smallScreenSize.value && showModal.value && modalReady.value );
const drawerPt = computed( () => compactBar.value ? { header: { class: 'hidden!' } } : undefined );
</script>

<template>
  <!-- Widths are !important because PrimeVue's unlayered `.p-drawer-right .p-drawer { width: 20rem }` outranks utilities.
       The width is the article column: .body-content's 72ch measure at prose-xl's 20px is 90ch at the drawer's 16px,
       plus the content's 1.25rem padding each side, so the title, byline and tickers line up with the text below them
       instead of spanning 85% of a wide screen over a narrow centred column. Never wider than 85% of the window.
       No @hide: every way of closing it (the X, Esc, the mask) goes through v-model, and a late hide from the leave
       transition would close an item opened straight after. Full-screen, the md: width stays off: a landscape phone is
       md-wide. -->
  <Drawer v-model:visible="drawerVisible" :position="smallScreenSize ? 'full' : 'right'" :block-scroll="true" :show-close-icon="!compactBar" :pt="drawerPt"
          :class="smallScreenSize ? 'w-full!' : 'w-full! md:w-[min(85%,calc(90ch_+_2.5rem))]!'">
    <ContentModal v-if="showModal" :key="modalKey" :compact="smallScreenSize" @ready="modalReady = true" @close="drawerVisible = false" />
    <ContentSkeleton v-else-if="isResolving" label="Loading preview…" />
  </Drawer>
</template>
